/* eslint-disable i18next/no-literal-string */
/* eslint-disable react-refresh/only-export-components */
import React, { Component, ReactNode, createContext, useContext } from "react";
import Logger from "@/utils/Logger";
import { logDiagnostic } from "@/utils/LogHelper";
import { ErrorSeverity, ErrorCategory } from "@/constants/errorConstants";

/**
 * Application error class with additional context
 * Single source of truth for error handling
 */
export class AppError extends Error {
  constructor(
    message: string,
    public readonly severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    public readonly category: ErrorCategory = ErrorCategory.UNKNOWN,
    public readonly recoverable: boolean = true,
    public readonly code?: string,
    public readonly context?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "AppError";
  }

  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message,
      severity: this.severity,
      category: this.category,
      recoverable: this.recoverable,
      code: this.code,
      context: this.context,
    };
  }
}

/**
 * Error information shape for context
 */
export interface ErrorInfo {
  error: Error;
  errorInfo?: React.ErrorInfo;
  timestamp: Date;
  context?: string;
}

/**
 * Context value shape for manual error reporting
 */
interface ErrorBoundaryContextValue {
  reportError: (error: unknown, context: string) => void;
  clearError: () => void;
}

/**
 * Context for manual error reporting from functional components
 */
const ErrorBoundaryContext = createContext<
  ErrorBoundaryContextValue | undefined
>(undefined);

/**
 * Hook to manually report errors from functional components
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { reportError } = useErrorBoundary();
 *
 *   const handleAction = async () => {
 *     try {
 *       await riskyOperation();
 *     } catch (error) {
 *       reportError(error, "MyComponent.handleAction");
 *     }
 *   };
 * };
 * ```
 */
export const useErrorBoundary = (): ErrorBoundaryContextValue => {
  const context = useContext(ErrorBoundaryContext);
  if (!context) {
    throw new Error(
      "useErrorBoundary must be used within an ErrorBoundary component",
    );
  }
  return context;
};

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (
    error: Error,
    errorInfo: React.ErrorInfo | undefined,
    reset: () => void,
  ) => ReactNode;
  onError?: (error: Error, errorInfo?: React.ErrorInfo) => void;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Unified Error Boundary Component
 *
 * Features:
 * 1. Catches React render errors automatically (componentDidCatch)
 * 2. Provides context for manual error reporting (useErrorBoundary hook)
 * 3. Integrates with backend diagnostic logging
 * 4. Supports custom fallback UI
 * 5. Allows error recovery/reset
 *
 * @example
 * ```tsx
 * // Automatic error catching
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 *
 * // Manual error reporting
 * const MyComponent = () => {
 *   const { reportError } = useErrorBoundary();
 *   // ...
 * };
 * ```
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Log error with appropriate severity and send to backend diagnostics
   */
  private logError = (
    error: Error,
    errorInfo?: React.ErrorInfo,
    context?: string,
  ): void => {
    const logContext =
      error instanceof AppError ? error.category : context || "ErrorBoundary";

    // Log based on severity
    if (error instanceof AppError) {
      switch (error.severity) {
        case ErrorSeverity.CRITICAL:
          Logger.error(
            `Critical ${error.category} error: ${error.message}`,
            error,
            ["ErrorBoundary", logContext],
            error.toJSON(),
          );
          break;
        case ErrorSeverity.HIGH:
          Logger.error(
            `High severity error: ${error.message}`,
            error,
            ["ErrorBoundary", logContext],
            error.toJSON(),
          );
          break;
        case ErrorSeverity.MEDIUM:
          Logger.warn(
            `Medium severity error: ${error.message}`,
            ["ErrorBoundary", logContext],
            error.toJSON(),
          );
          break;
        default:
          Logger.info(`Low severity error: ${error.message}`, [
            "ErrorBoundary",
            logContext,
          ]);
      }
    } else {
      Logger.error(
        `Unhandled error: ${error.message}`,
        error,
        ["ErrorBoundary", logContext],
        errorInfo ? { componentStack: errorInfo.componentStack } : undefined,
      );
    }

    // Send to backend diagnostics (fails silently)
    logDiagnostic(error, logContext).catch(() => {
      Logger.debug("Failed to log error to backend diagnostics", [
        "ErrorBoundary",
      ]);
    });
  };

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo });

    // Log the error
    this.logError(error, errorInfo);

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  /**
   * Manual error reporting for async operations, event handlers, etc.
   */
  private reportError = (err: unknown, context: string): void => {
    const error = err instanceof Error ? err : new Error(String(err));

    this.setState({
      hasError: true,
      error,
      errorInfo: null,
    });

    // Log the error
    this.logError(error, undefined, context);

    // Call optional error handler
    this.props.onError?.(error);
  };

  private clearError = (): void => {
    Logger.debug("Clearing error state", ["ErrorBoundary"]);
    this.props.onReset?.();
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback } = this.props;

    // Provide context value for manual error reporting
    const contextValue: ErrorBoundaryContextValue = {
      reportError: this.reportError,
      clearError: this.clearError,
    };

    // Show error UI if error exists
    if (hasError && error) {
      const errorFallback = fallback ? (
        fallback(error, errorInfo || undefined, this.clearError)
      ) : (
        <DefaultErrorFallback error={error} reset={this.clearError} />
      );

      // Still provide context even in error state for child fallback components
      return (
        <ErrorBoundaryContext.Provider value={contextValue}>
          {errorFallback}
        </ErrorBoundaryContext.Provider>
      );
    }

    // Normal render with context
    return (
      <ErrorBoundaryContext.Provider value={contextValue}>
        {children}
      </ErrorBoundaryContext.Provider>
    );
  }
}

/**
 * Default error fallback UI component
 */
const DefaultErrorFallback: React.FC<{
  error: Error;
  reset: () => void;
}> = ({ error, reset }) => {
  const isAppError = error instanceof AppError;
  const severity = isAppError ? error.severity : ErrorSeverity.MEDIUM;
  const category = isAppError ? error.category : ErrorCategory.UNKNOWN;

  const getSeverityColor = (): string => {
    switch (severity) {
      case ErrorSeverity.CRITICAL:
        return "text-red-700 bg-red-50 border-red-300";
      case ErrorSeverity.HIGH:
        return "text-orange-700 bg-orange-50 border-orange-300";
      case ErrorSeverity.MEDIUM:
        return "text-yellow-700 bg-yellow-50 border-yellow-300";
      default:
        return "text-blue-600 bg-blue-50 border-blue-600";
    }
  };

  const getCategoryIcon = (): string => {
    switch (category) {
      case ErrorCategory.NETWORK:
        return "icon-cloud_off";
      case ErrorCategory.AUTHENTICATION:
      case ErrorCategory.AUTHORIZATION:
        return "icon-lock";
      case ErrorCategory.VALIDATION:
        return "icon-warning";
      default:
        return "icon-error";
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
      <div
        className={`max-w-2xl w-full p-8 border-2 rounded-lg shadow-lg ${getSeverityColor()}`}
      >
        <div className="flex items-center mb-4">
          <span className={`${getCategoryIcon()} text-4xl mr-4`} />
          <div>
            <h1 className="text-2xl font-bold mb-2">
              {isAppError
                ? `${category.toUpperCase()} Error`
                : "Something went wrong"}
            </h1>
            <p className="text-lg font-medium">{error.message}</p>
          </div>
        </div>

        {isAppError && error.code && (
          <div className="mb-4">
            <span className="font-semibold">Error Code:</span> {error.code}
          </div>
        )}

        {process.env.NODE_ENV === "development" && error.stack && (
          <details className="mt-4">
            <summary className="cursor-pointer font-semibold mb-2">
              Stack Trace (Development Only)
            </summary>
            <pre className="text-sm overflow-auto bg-gray-900 text-gray-100 p-4 rounded">
              {error.stack}
            </pre>
          </details>
        )}

        <div className="mt-6 flex gap-4">
          <button
            onClick={reset}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors font-medium"
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors font-medium"
          >
            Reload Page
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundary;
